<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid, useDirtyFormGuard } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import type { AbuseCase, AbuseCaseUpdate } from '@/types/compliance'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AbuseDetail' })

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const saving = ref(false)
const abuseCase = ref<AbuseCase | null>(null)

const editedStatus = ref<string>('')
const editedResolution = ref<string>('')

const caseId = computed(() => Number(route.params.id))

const statusOptions = [
  { value: 'open', title: 'Offen' },
  { value: 'investigating', title: 'In Prüfung' },
  { value: 'resolved', title: 'Gelöst' },
  { value: 'rejected', title: 'Abgewiesen' }
]

const categoryLabels: Record<string, string> = {
  spam: 'Spam',
  phishing: 'Phishing',
  malware: 'Malware',
  fake_data: 'Falsche Daten',
  trademark: 'Markenrecht',
  other: 'Sonstiges'
}

async function fetchAbuseCase() {
  loading.value = true
  try {
    const res = await useAxios<null, AbuseCase>(
      'GET',
      `/admin/abuse/${caseId.value}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data) {
      abuseCase.value = res.data
      editedStatus.value = res.data.status
      editedResolution.value = res.data.resolution_text || ''
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AbuseDetail' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    loading.value = false
  }
}

async function saveChanges() {
  if (!abuseCase.value) return

  saving.value = true
  try {
    const updateData: AbuseCaseUpdate = {
      status: editedStatus.value as AbuseCaseUpdate['status'],
      resolution_text: editedResolution.value || undefined
    }

    const res = await useAxios<AbuseCaseUpdate, AbuseCase>(
      'PUT',
      `/admin/abuse/${caseId.value}`,
      true,
      updateData
    )

    if (res && res.status === STATUS_CODES.OK && res.data) {
      abuseCase.value = res.data
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: 'Gespeichert',
        message: 'Der Fall wurde erfolgreich aktualisiert.'
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AbuseDetail' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    saving.value = false
  }
}

function formatDate(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'open': return 'status-open'
    case 'investigating': return 'status-investigating'
    case 'resolved': return 'status-resolved'
    case 'rejected': return 'status-rejected'
    default: return ''
  }
}

function getCategoryClass(category: string): string {
  switch (category) {
    case 'spam': return 'category-spam'
    case 'phishing': return 'category-phishing'
    case 'malware': return 'category-malware'
    case 'fake_data': return 'category-fake'
    case 'trademark': return 'category-trademark'
    default: return 'category-other'
  }
}

const hasChanges = computed(() => {
  if (!abuseCase.value) return false
  return (
    editedStatus.value !== abuseCase.value.status ||
    editedResolution.value !== (abuseCase.value.resolution_text || '')
  )
})

// Register dirty form guard for navigation warnings
useDirtyFormGuard('abuse-detail', hasChanges)

onMounted(() => {
  fetchAbuseCase()
})
</script>

<template>
  <BaseDashboardPage
    :title="t('dashboard.admin.compliance.abuse.detail.title')"
    :subtitle="t('dashboard.admin.compliance.abuse.detail.subtitle', { id: caseId })"
    icon="mdi-alert-circle-outline"
    icon-color="red"
    show-back
    :back-route="{ name: 'dashboardAdminAbuse' }"
    max-width="1200px"
  >
    <template #actions>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!hasChanges || loading"
        @click="saveChanges"
      >
        <v-icon icon="mdi-content-save" class="me-2" />
        {{ t('dashboard.admin.compliance.abuse.detail.save') }}
      </v-btn>
    </template>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Content -->
    <div v-else-if="abuseCase" class="detail-content">
      <!-- Info Cards Row -->
      <div class="info-grid">
        <!-- Case Info Card -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.compliance.abuse.detail.caseInfo') }}</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.created') }}</span>
              <span class="info-value">{{ formatDate(abuseCase.created) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.category') }}</span>
              <span class="category-badge" :class="getCategoryClass(abuseCase.category)">
                {{ categoryLabels[abuseCase.category] || abuseCase.category }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.currentStatus') }}</span>
              <span class="status-badge" :class="getStatusClass(abuseCase.status)">
                {{ statusOptions.find(s => s.value === abuseCase?.status)?.title || abuseCase.status }}
              </span>
            </div>
          </div>
        </div>

        <!-- Domain Card -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.compliance.abuse.detail.domain') }}</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.domainName') }}</span>
              <span class="info-value domain-value">{{ abuseCase.domain_name || '-' }}</span>
            </div>
          </div>
        </div>

        <!-- Reporter Card -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.compliance.abuse.detail.reporter') }}</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.reporterEmail') }}</span>
              <span class="info-value">{{ abuseCase.reporter_email }}</span>
            </div>
            <div v-if="abuseCase.reporter_name" class="info-row">
              <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.reporterName') }}</span>
              <span class="info-value">{{ abuseCase.reporter_name }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Description Card -->
      <div class="description-card">
        <h3 class="card-title">{{ t('dashboard.admin.compliance.abuse.detail.description') }}</h3>
        <div class="description-content">
          {{ abuseCase.description }}
        </div>
      </div>

      <!-- Actions Card -->
      <div class="actions-card">
        <h3 class="card-title">{{ t('dashboard.admin.compliance.abuse.detail.actions') }}</h3>
        <div class="action-fields">
          <v-select
            v-model="editedStatus"
            :items="statusOptions"
            item-title="title"
            item-value="value"
            :label="t('dashboard.admin.compliance.abuse.detail.newStatus')"
            variant="outlined"
            density="comfortable"
            class="status-select"
          />
          <v-textarea
            v-model="editedResolution"
            :label="t('dashboard.admin.compliance.abuse.detail.resolutionNote')"
            :placeholder="t('dashboard.admin.compliance.abuse.detail.resolutionPlaceholder')"
            variant="outlined"
            rows="4"
            class="resolution-textarea"
          />
        </div>
      </div>

      <!-- Resolution Info (if resolved) -->
      <div v-if="abuseCase.resolved_at" class="resolution-card">
        <h3 class="card-title">{{ t('dashboard.admin.compliance.abuse.detail.resolution') }}</h3>
        <div class="info-rows">
          <div class="info-row">
            <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.resolvedAt') }}</span>
            <span class="info-value">{{ formatDate(abuseCase.resolved_at) }}</span>
          </div>
          <div v-if="abuseCase.resolved_by" class="info-row">
            <span class="info-label">{{ t('dashboard.admin.compliance.abuse.detail.resolvedBy') }}</span>
            <span class="info-value">{{ abuseCase.resolved_by }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="empty-state">
      <v-icon icon="mdi-alert-circle-outline" size="64" />
      <h3>{{ t('dashboard.admin.compliance.abuse.detail.notFound') }}</h3>
      <p>{{ t('dashboard.admin.compliance.abuse.detail.notFoundDescription') }}</p>
      <v-btn
        color="primary"
        variant="outlined"
        @click="router.push({ name: 'dashboardAdminAbuse' })"
      >
        {{ t('dashboard.admin.compliance.abuse.detail.backToList') }}
      </v-btn>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Loading/Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(239, 83, 80, 0.5);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin: 0 0 24px;
  }
}

/* Content */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

/* Cards */
.info-card,
.description-card,
.actions-card,
.resolution-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.card-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.info-label {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.info-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: right;
}

.domain-value {
  font-weight: 600;
  color: #00B0FF;
}

/* Description */
.description-content {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  white-space: pre-wrap;
}

/* Actions */
.action-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-select {
  max-width: 300px;
}

/* Category Badges */
.category-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.category-spam {
    background: rgba(158, 158, 158, 0.15);
    color: #bdbdbd;
  }

  &.category-phishing {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.category-malware {
    background: rgba(156, 39, 176, 0.15);
    color: #ce93d8;
  }

  &.category-fake {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.category-trademark {
    background: rgba(33, 150, 243, 0.15);
    color: #64b5f6;
  }

  &.category-other {
    background: rgba(120, 163, 255, 0.15);
    color: #90caf9;
  }
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.status-open {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-investigating {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-resolved {
    background: rgba(76, 175, 80, 0.15);
    color: #4caf50;
  }

  &.status-rejected {
    background: rgba(158, 158, 158, 0.15);
    color: #9e9e9e;
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .info-card,
  .description-card,
  .actions-card,
  .resolution-card {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .card-title {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-label {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-value {
    color: rgba(15, 23, 42, 0.9);
  }

  .description-content {
    color: rgba(15, 23, 42, 0.85);
  }

  .empty-state {
    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

/* Mobile */
@media (max-width: 768px) {
  .abuse-detail {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .info-value {
    text-align: left;
  }

  .status-select {
    max-width: 100%;
  }
}
</style>
